import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import { useCmsImageUpload } from "../../../hooks/useCmsImageUpload";
import { validateCmsImageFile } from "../utils/cmsValidation";

function copyText(text: string, label: string) {
  void navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copied`),
    () => toast.error("Could not copy — try selecting the text manually.")
  );
}

interface CmsImageUploadPanelProps {
  pageSlug: string;
  disabled?: boolean;
}

const CmsImageUploadPanel: React.FC<CmsImageUploadPanelProps> = ({
  pageSlug,
  disabled,
}) => {
  const { uploadImage, isLoading, error, resetError, lastUploadDetails, clearLastDetails } =
    useCmsImageUpload();
  const [localErr, setLocalErr] = useState<string | null>(null);

  const folder = pageSlug ? `public/pages/${pageSlug}` : "";

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || disabled || !pageSlug) return;
    resetError();
    clearLastDetails();
    setLocalErr(null);
    const v = validateCmsImageFile(file);
    if (v) {
      setLocalErr(v);
      return;
    }
    await uploadImage(file, pageSlug);
  };

  const d = lastUploadDetails;
  const imageSnippet = d
    ? JSON.stringify({ fileKey: d.fileKey, alt: "Describe this image" }, null, 2)
    : "";
  const summaryForJson = d
    ? JSON.stringify(
        {
          note: "Use fileKey (+ alt) in body_json or seo_json. Do not save publicUrl in the API.",
          folder: d.folder,
          fileKey: d.fileKey,
          publicUrl_previewOnly: d.publicUrl,
          originalFileName: d.originalFileName,
          contentType: d.contentType,
          uploadUrl: d.uploadUrl,
        },
        null,
        2
      )
    : "";

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Image upload (S3)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Images upload directly to storage. After a successful upload, copy{" "}
        <code>fileKey</code> (and set <code>alt</code>) into your <code>body_json</code> or{" "}
        <code>seo_json</code> below. Folder used:{" "}
        <code>{folder || "(save page first — slug required)"}</code>
      </Typography>

      <Button variant="outlined" component="label" disabled={disabled || isLoading || !pageSlug}>
        {isLoading ? "Uploading…" : "Choose image file"}
        <input type="file" hidden accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml" onChange={handleFile} />
      </Button>

      {(localErr || error) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {localErr || error}
        </Alert>
      )}

      {d ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Paste into JSON
          </Typography>
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
            <Box
              component="pre"
              sx={{
                flex: 1,
                m: 0,
                p: 1.5,
                bgcolor: "grey.100",
                borderRadius: 1,
                fontSize: 12,
                overflow: "auto",
                maxHeight: 160,
              }}
            >
              {imageSnippet}
            </Box>
            <Tooltip title="Copy image object">
              <IconButton
                size="small"
                onClick={() => copyText(imageSnippet, "Image JSON snippet")}
                aria-label="Copy image JSON snippet"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            <Button size="small" variant="text" onClick={() => copyText(d.fileKey, "fileKey")}>
              Copy fileKey
            </Button>
            <Button size="small" variant="text" onClick={() => copyText(d.publicUrl, "publicUrl")}>
              Copy publicUrl (preview)
            </Button>
            <Button size="small" variant="text" onClick={() => copyText(d.folder, "folder")}>
              Copy folder
            </Button>
          </Stack>

          <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: "divider" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">Full upload response (copy for debugging)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box
                  component="pre"
                  sx={{
                    flex: 1,
                    m: 0,
                    p: 1,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    fontSize: 11,
                    overflow: "auto",
                    maxHeight: 360,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {summaryForJson}
                </Box>
                <Tooltip title="Copy full JSON">
                  <IconButton
                    size="small"
                    onClick={() => copyText(summaryForJson, "Full upload JSON")}
                    aria-label="Copy full upload JSON"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      ) : null}
    </Paper>
  );
};

export default CmsImageUploadPanel;
